import { splitTickTextIntoLines } from './splitTickTextIntoLines';

import type { ReportWidgetChartPoint } from '../types';

export const BAR_CHART_MIN_HEIGHT_PX = 8;

export const scaleBarHeightFromFloor = ({
    linearHeight,
    plotHeight,
    minHeight = BAR_CHART_MIN_HEIGHT_PX,
}: {
    linearHeight: number;
    plotHeight: number;
    minHeight?: number;
}): number => {
    if (!Number.isFinite(linearHeight) || linearHeight <= 0) {
        return 0;
    }

    if (!Number.isFinite(plotHeight) || plotHeight <= 0) {
        return Math.max(linearHeight, minHeight);
    }

    const floor = Math.min(minHeight, plotHeight);
    const ratio = Math.min(linearHeight / plotHeight, 1);

    return floor + ratio * (plotHeight - floor);
};

export interface BarChartWrappedTickPreview {
    formatted: string;
    split: {
        lines: string[];
        wasTruncated: boolean;
    };
}

interface BuildBarChartWrappedTickPreviewParams {
    data: ReportWidgetChartPoint[];
    shouldWrapTicks: boolean;
    xAxisTickFormatter?: (
        label: string | number,
        index: number,
        data: ReportWidgetChartPoint[],
    ) => string;
    xAxisTickWrap?: {
        maxLines?: number;
        maxCharsPerLine?: number;
    };
}

export const shouldRotateBarChartTicks = (
    data: ReportWidgetChartPoint[],
    xAxisTickAngle?: number,
): boolean => {
    if (xAxisTickAngle !== undefined) {
        return xAxisTickAngle !== 0;
    }

    // Keep labels horizontal by default; rotate only when label density/length is likely to collide.
    const maxLabelLength = data.reduce(
        (max, point) => Math.max(max, point.name.length),
        0,
    );

    return data.length >= 8 || (maxLabelLength >= 14 && data.length >= 5);
};

export const buildBarChartWrappedTickPreview = ({
    data,
    shouldWrapTicks,
    xAxisTickFormatter,
    xAxisTickWrap,
}: BuildBarChartWrappedTickPreviewParams): BarChartWrappedTickPreview[] => {
    // Precompute formatted + split labels once so render and layout decisions reuse the same result.
    if (!shouldWrapTicks) {
        return [];
    }

    const maxCharsPerLine = xAxisTickWrap?.maxCharsPerLine ?? 24;
    const maxLines = xAxisTickWrap?.maxLines ?? 2;

    return data.map((point, index) => {
        const formatted = xAxisTickFormatter
            ? xAxisTickFormatter(point.name, index, data)
            : String(point.name);

        return {
            formatted,
            split: splitTickTextIntoLines(formatted, maxLines, maxCharsPerLine),
        };
    });
};

export const resolveBarChartXAxisHeight = (params: {
    shouldRotateTicks: boolean;
    shouldWrapTicks: boolean;
    wrappedTicks: BarChartWrappedTickPreview[];
}): number => {
    // Favor readability in this order: rotated labels, true 2-line wrap, then compact single-line default.
    const { shouldRotateTicks, shouldWrapTicks, wrappedTicks } = params;
    const hasWrappedSecondLine =
        !shouldRotateTicks &&
        wrappedTicks.some((tick) => tick.split.lines.length > 1);

    if (shouldRotateTicks) {
        return shouldWrapTicks ? 71 : 68;
    }

    if (shouldWrapTicks && hasWrappedSecondLine) {
        return 40;
    }

    return 30;
};
